import React, { FunctionComponent, useState } from 'react';
import { canSideRotationIsAvailable } from 'src/components/pages/products/helpers';
import { getStartingUnit, Unit } from 'src/components/widgets/areaDesigner/helpers';
import Loader from 'src/components/widgets/loader';
import { useUpgradeDialogs } from 'src/shared/dialogs';
import { T } from 'src/shared/helpers';
import MdAdd from 'src/shared/material_icons/content/add.svg';
import MdDelete from 'src/shared/material_icons/action/delete.svg';
import MdSwapHoriz from 'src/shared/material_icons/action/swap_horiz.svg';
import MdAutoAwesome from 'src/shared/material_icons/image/auto_awesome.svg';
import MdCropFree from 'src/shared/material_icons/image/crop_free.svg';
import MdTune from 'src/shared/material_icons/image/tune.svg';
import styled from 'styled-components';
import { GhostButton, Link } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { useDropdown } from '../../../../widgets/dropdown';
import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { Side } from '../../state/side';
import PrintAreaPreview from '../3-printAreas/printAreaPreview';

const Cell = styled.div`
    flex: 1;
    border: 1px ${(props) => props.theme.app.primary_light} dotted;
    display: block;
    flex-direction: column;

    &:not(:last-child) {
        border-right: none;
    }
`;

const Header = styled.div<{ $disabled: boolean }>`
    font-weight: ${(props) => props.theme.text.boldWeight};
    border-bottom: 1px ${(props) => props.theme.app.primary_light} dotted;
    min-height: 40px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 2px;
    overflow: hidden;
    padding: 6px 44px 6px 20px;

    ${(props) =>
        props.$disabled &&
        `
        span {
            color: #CCC;
        }
    `}
`;

const SideName = styled.span`
    display: block;
    width: 100%;
    line-height: 18px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const SideCode = styled.span`
    display: block;
    width: 100%;
    font-size: 11px;
    line-height: 1.3;
    font-weight: 400;
    color: #78909c;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const HeaderDivider = styled.div`
    width: 1px;
    height: 16px;
    background: #cfd8dc;
    flex-shrink: 0;
`;

const AiHeaderButton = styled(GhostButton)`
    color: #7c3aed !important;
`;

const HeaderActions = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 8px;
    display: flex;
    align-items: center;
    gap: 2px;
`;

const Content = styled.div<{ $compact?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    height: ${(props) => (props.$compact ? '150px' : '250px')};
    padding: ${(props) => (props.$compact ? '12px' : '20px')};
    user-select: none;
`;

const ImageContent = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`;

// The print-area chip floats over the bottom of the image so it doesn't steal vertical space from it.
const ChipOverlay = styled.div`
    position: absolute;
    left: 50%;
    bottom: 4px;
    transform: translateX(-50%);
    z-index: 1;
`;

const AddIcon = styled.div`
    ${Icon} {
        width: 35px;
        height: 35px;
    }
`;

const PrintAreaChip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    background: #fff;
    border: 1px solid #cfd8dc;
    border-radius: 20px;
    font-size: 12px;
    color: #546e7a;
    cursor: pointer;
    /* Never wrap: the dimensions text (e.g. 3-digit values with 3 decimals) must stay on one line. */
    white-space: nowrap;
    max-width: none;
    transition:
        background 0.15s,
        border-color 0.15s;

    &:hover {
        background: #f5f5f5;
        border-color: #90a4ae;
    }

    span {
        white-space: nowrap;
    }

    ${Icon} {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
    }
`;

const PrintAreaBorder = styled.div`
    height: 100%;
    position: relative;
`;

const SideOptionsDropdownContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 6px;
    min-width: 260px;
`;

interface VariantCellProps {
    side?: Side;
    modelColor: ModelColor;
    modelSide?: ModelSide | null;
    isToggleEnabled?: boolean;
    isAreaChangeEnabled?: boolean;
    dummy?: boolean;
    compact?: boolean;
    onImageChangeClick?: () => void;
    onAiGenerateClick?: () => void;
    onAreaChangeClick?: () => void;
    onToggleClick?: () => void;
    onSetMandatorySide?: (value: boolean) => void;
    onSetRotateSide?: (value: boolean) => void;
    editOnlyVariants?: boolean;
}

const VariantCell: FunctionComponent<VariantCellProps> = ({
    side,
    modelColor,
    modelSide,
    dummy,
    compact,
    isToggleEnabled,
    isAreaChangeEnabled,
    onImageChangeClick,
    onAiGenerateClick,
    onToggleClick,
    onAreaChangeClick,
    onSetMandatorySide,
    onSetRotateSide,
    editOnlyVariants
}) => {
    const { state } = useProductConfigurationState();
    const [unit] = useState<Unit>(getStartingUnit());
    const { getUploadStatus } = useModelSideManager();
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const [openSideOptionsDropdown, closeSideOptionsDropdown, isSideOptionsDropdownOpen, SideOptionsDropdown] = useDropdown();

    const uploadStatus = modelSide ? getUploadStatus(modelSide) : { uploading: false, progress: 0 };
    const disabled = !modelSide || !!state.disabledModelSides.find((x) => x.colorGuid == modelColor.guid && x.sideGuid == modelSide.originalSideGuid);

    const [canUseRotationOrMandatory, textDisable] = canSideRotationIsAvailable(state.product, modelSide, modelColor);

    const handleChangeImageClick = () => {
        if (modelSide && onImageChangeClick) {
            onImageChangeClick();
        }
    };

    const handleAiGenerateClick = () => {
        if (modelSide && onAiGenerateClick) {
            onAiGenerateClick();
        }
    };

    const handleChangePrintAreaClick = () => {
        if (isAreaChangeEnabled) {
            if (modelSide && onAreaChangeClick) {
                onAreaChangeClick();
            }
        } else {
            showPlanUpgradeDialog(T._('To create different print areas for different variants you must upgrade your plan.', 'Admin'));
        }
    };

    const handleToggleClick = () => {
        if (isToggleEnabled) {
            onToggleClick?.();
        } else {
            showPlanUpgradeDialog(T._('To disable a side for a specific variant you must upgrade your plan.', 'Admin'));
        }
    };

    const getPrintAreaButtonText = (modelSide?: ModelSide | null) => {
        let buttonText = T._('Add print area', 'Admin');
        if (modelSide && modelSide.areas.length > 0) {
            buttonText =
                (Math.round((modelSide.areas[0].boundsWidth / modelSide.ppcm / unit.ratio) * 10) / 10).toString() +
                ' × ' +
                (Math.round((modelSide.areas[0].boundsHeight / modelSide.ppcm / unit.ratio) * 10) / 10).toString() +
                ' ' +
                unit.label;
        }
        return buttonText;
    };

    return (
        <Cell>
            <Header $disabled={disabled}>
                <SideName>{side?.name}</SideName>
                {modelSide?.code && (
                    <SideCode>
                        {T._('Code', 'Admin')}: {modelSide.code}
                    </SideCode>
                )}
                {!dummy && !editOnlyVariants && (
                    <HeaderActions>
                        {!disabled && (
                            <>
                                <ZTooltip title={T._('Change image', 'Admin')}>
                                    <GhostButton
                                        type='button'
                                        $size='small'
                                        $type='icon'
                                        aria-label={T._('Change image', 'Admin')}
                                        onClick={handleChangeImageClick}
                                    >
                                        <Icon>
                                            <MdSwapHoriz />
                                        </Icon>
                                    </GhostButton>
                                </ZTooltip>
                                <ZTooltip title={T._('Generate image with AI', 'Admin')}>
                                    <AiHeaderButton
                                        type='button'
                                        $size='small'
                                        $type='icon'
                                        aria-label={T._('Generate image with AI', 'Admin')}
                                        onClick={handleAiGenerateClick}
                                    >
                                        <Icon>
                                            <MdAutoAwesome />
                                        </Icon>
                                    </AiHeaderButton>
                                </ZTooltip>
                                <HeaderDivider />
                                <ZTooltip title={T._('Edit print area', 'Admin')}>
                                    <GhostButton
                                        type='button'
                                        $size='small'
                                        $type='icon'
                                        aria-label={T._('Edit print area', 'Admin')}
                                        onClick={handleChangePrintAreaClick}
                                    >
                                        <Icon>
                                            <MdCropFree />
                                        </Icon>
                                    </GhostButton>
                                </ZTooltip>
                                <HeaderDivider />
                                {modelSide && (
                                    <>
                                        <GhostButton
                                            type='button'
                                            $size='small'
                                            $type='icon'
                                            aria-label={T._('Side options', 'Admin')}
                                            aria-expanded={isSideOptionsDropdownOpen}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                isSideOptionsDropdownOpen ? closeSideOptionsDropdown() : openSideOptionsDropdown(e.currentTarget, 'bottom-end');
                                            }}
                                        >
                                            <Icon>
                                                <MdTune />
                                            </Icon>
                                        </GhostButton>
                                        {isSideOptionsDropdownOpen && (
                                            <SideOptionsDropdown minWidth='260px' surfacePadding='6px'>
                                                <SideOptionsDropdownContent>
                                                    <Checkbox
                                                        label={T._('Side rotation in UI', 'Admin')}
                                                        checked={modelSide?.canRotate || false}
                                                        disabled={!canUseRotationOrMandatory}
                                                        disabledTooltip={textDisable}
                                                        onChange={(e) => {
                                                            closeSideOptionsDropdown();
                                                            onSetRotateSide?.(e);
                                                        }}
                                                        caption={
                                                            <>
                                                                {T._('Let customers rotate the product in the customizer.', 'Admin')}{' '}
                                                                <Link
                                                                    target='_blank'
                                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/11739151453468-Side-rotation-in-UI'
                                                                >
                                                                    {T._('Learn more', 'Admin')}
                                                                </Link>
                                                            </>
                                                        }
                                                    />
                                                    {state.sides.length > 1 && (
                                                        <Checkbox
                                                            label={T._('Customization mandatory', 'Admin')}
                                                            checked={modelSide.mandatory || false}
                                                            onChange={(value) => {
                                                                closeSideOptionsDropdown();
                                                                onSetMandatorySide?.(value);
                                                            }}
                                                            caption={T._('Customers must customize this side before purchasing.', 'Admin')}
                                                        />
                                                    )}
                                                </SideOptionsDropdownContent>
                                            </SideOptionsDropdown>
                                        )}
                                        <HeaderDivider />
                                    </>
                                )}
                            </>
                        )}
                        <ZTooltip title={!disabled ? T._('Delete this side for this variant', 'Admin') : undefined}>
                            <GhostButton
                                type='button'
                                $size='small'
                                $type='icon'
                                aria-label={disabled ? T._('Enable the side for the variant', 'Admin') : T._('Delete this side for this variant', 'Admin')}
                                onClick={handleToggleClick}
                            >
                                <Icon>
                                    <MdDelete />
                                </Icon>
                            </GhostButton>
                        </ZTooltip>
                    </HeaderActions>
                )}
            </Header>

            <Content $compact={compact}>
                <ImageContent>
                    {!dummy && disabled && (
                        <AddIcon>
                            <Icon $hoverable onClick={handleToggleClick}>
                                <MdAdd />
                            </Icon>
                        </AddIcon>
                    )}
                    {modelSide && !disabled && !uploadStatus.uploading && (
                        <PrintAreaBorder>
                            <PrintAreaPreview modelSide={modelSide} product={state.product}></PrintAreaPreview>
                        </PrintAreaBorder>
                    )}
                    {modelSide && !disabled && uploadStatus.uploading && uploadStatus.uploading && <Loader />}

                    {!dummy && !editOnlyVariants && !disabled && modelSide && (
                        <ChipOverlay>
                            <PrintAreaChip onClick={handleChangePrintAreaClick}>
                                <Icon>
                                    <MdCropFree />
                                </Icon>
                                <span>{getPrintAreaButtonText(modelSide)}</span>
                            </PrintAreaChip>
                        </ChipOverlay>
                    )}
                </ImageContent>
            </Content>
        </Cell>
    );
};

export default VariantCell;
